import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { CreditCard, MessageSquare, Mail, CheckCircle, XCircle, Loader2, Zap, Wallet, Building, Store, Banknote, Copy, Trash2, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function TenantSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => { 
    api.get('/tenant/settings').then(r => { 
      setSettings(r.data); 
      setLoading(false); 
    }).catch(() => setLoading(false)); 
  }, []);

  const save = async (data) => {
    setSaving(true);
    try { 
      await api.put('/tenant/settings', data);
      const refreshed = await api.get('/tenant/settings');
      setSettings(refreshed.data);
      toast({ title: 'Berhasil', description: 'Pengaturan berhasil disimpan' });
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Gagal menyimpan',
        variant: 'destructive',
      });
    } finally { 
      setSaving(false); 
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  const integration = settings?.integration || {};

  return (
    <div data-testid="tenant-settings-page">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-6">Integrasi & Pengaturan</h1>
      <Tabs defaultValue="pakasir">
        <TabsList className="mb-6">
          <TabsTrigger value="pakasir" className="gap-2"><CreditCard size={14} /> Pakasir</TabsTrigger>
          <TabsTrigger value="tripay" className="gap-2"><CreditCard size={14} /> Tripay</TabsTrigger>
          <TabsTrigger value="moota" className="gap-2"><Banknote size={14} /> Moota</TabsTrigger>
          <TabsTrigger value="payment-methods" className="gap-2"><Wallet size={14} /> Metode Bayar</TabsTrigger>
          <TabsTrigger value="responic" className="gap-2"><MessageSquare size={14} /> Responic</TabsTrigger>
          <TabsTrigger value="myerketing" className="gap-2"><Mail size={14} /> Email SMTP</TabsTrigger>
        </TabsList>

        <TabsContent value="pakasir">
          <PakasirSettings data={integration.pakasir || {}} onSave={(d) => save({ pakasir: d })} saving={saving} />
        </TabsContent>
        <TabsContent value="tripay">
          <TripayGatewaySettings data={integration.tripay_gateway || {}} onSave={(d) => save({ tripay_gateway: d })} saving={saving} />
        </TabsContent>
        <TabsContent value="moota">
          <MootaSettings />
        </TabsContent>
        <TabsContent value="payment-methods">
          <PaymentMethodsSettings />
        </TabsContent>
        <TabsContent value="responic">
          <ResponicSettings data={integration.responic || integration.starsender || {}} onSave={(d) => save({ responic: d })} saving={saving} />
        </TabsContent>
        <TabsContent value="myerketing">
          <EmailSettings data={integration.myerketing || {}} onSave={(d) => save({ myerketing: d })} saving={saving} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PakasirSettings({ data, onSave, saving }) {
  const [form, setForm] = useState({ slug: data.slug || '', api_key: data.api_key || '', mode: data.mode || 'sandbox', is_active: data.is_active || false });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const { toast } = useToast();

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/tenant/integrations/test-connection', { integration_type: 'pakasir' });
      setTestResult(res.data);
      if (res.data.success) {
        toast({ title: 'Berhasil', description: 'Koneksi Pakasir berhasil' });
      } else {
        toast({ title: 'Gagal', description: res.data.message, variant: 'destructive' });
      }
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Gagal test koneksi' });
      toast({ title: 'Error', description: 'Gagal test koneksi', variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><CreditCard size={18} className="text-[#2D6CDF]" /> Payment Gateway - Pakasir</CardTitle>
        <CardDescription>Konfigurasi payment gateway untuk menerima pembayaran</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /><Label>Aktifkan Pakasir</Label></div>
        <div><Label>Project Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="project-slug" data-testid="pakasir-slug-input" /></div>
        <div><Label>API Key</Label><Input value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="xxx123" data-testid="pakasir-apikey-input" type="password" /></div>
        <div className="flex items-center gap-4">
          <Label>Mode:</Label>
          <Button variant={form.mode === 'sandbox' ? 'default' : 'outline'} size="sm" onClick={() => setForm({ ...form, mode: 'sandbox' })} className={form.mode === 'sandbox' ? 'bg-amber-500' : ''}>Sandbox</Button>
          <Button variant={form.mode === 'production' ? 'default' : 'outline'} size="sm" onClick={() => setForm({ ...form, mode: 'production' })} className={form.mode === 'production' ? 'bg-emerald-500' : ''}>Production</Button>
        </div>
        
        {/* Test Result */}
        {testResult && (
          <div className={`p-3 rounded-lg flex items-center gap-2 ${testResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {testResult.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
            {testResult.message}
          </div>
        )}

        <div className="flex gap-2">
          <Button onClick={() => onSave(form)} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={saving} data-testid="save-pakasir-btn">
            {saving && <Loader2 size={14} className="animate-spin mr-2" />} Simpan
          </Button>
          <Button variant="outline" onClick={testConnection} disabled={testing || !form.api_key} data-testid="test-pakasir-btn">
            {testing ? <Loader2 size={14} className="animate-spin mr-2" /> : <Zap size={14} className="mr-2" />} Test Koneksi
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function TripayGatewaySettings({ data, onSave, saving }) {
  const [form, setForm] = useState({
    api_key: data.api_key || '',
    private_key: data.private_key || '',
    merchant_code: data.merchant_code || '',
    mode: data.mode || 'production',
    is_active: data.is_active || false,
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const { toast } = useToast();

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/tenant/integrations/test-connection', { integration_type: 'tripay_gateway' });
      setTestResult(res.data);
      if (res.data.success) {
        toast({ title: 'Berhasil', description: 'Koneksi Tripay berhasil' });
      } else {
        toast({ title: 'Gagal', description: res.data.message, variant: 'destructive' });
      }
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Gagal test koneksi' });
      toast({ title: 'Error', description: 'Gagal test koneksi', variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard size={18} className="text-[#6C5CE7]" /> Payment Gateway - Tripay
        </CardTitle>
        <CardDescription>
          Konfigurasi Tripay.co.id untuk menerima pembayaran via QRIS, Virtual Account, E-Wallet, dll.
          Daftar di <a href="https://tripay.co.id" target="_blank" rel="noopener noreferrer" className="text-[#2D6CDF] underline">tripay.co.id</a>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
          <Label>Aktifkan Tripay</Label>
        </div>
        <div>
          <Label>API Key</Label>
          <Input value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="DEV-xxxxxxxxxxxx" type="password" />
        </div>
        <div>
          <Label>Private Key</Label>
          <Input value={form.private_key} onChange={(e) => setForm({ ...form, private_key: e.target.value })} placeholder="xxxxxxxxxxxxxxxx" type="password" />
          <p className="text-xs text-muted-foreground mt-1">Digunakan untuk verifikasi signature callback webhook.</p>
        </div>
        <div>
          <Label>Merchant Code</Label>
          <Input value={form.merchant_code} onChange={(e) => setForm({ ...form, merchant_code: e.target.value })} placeholder="T12345" />
          <p className="text-xs text-muted-foreground mt-1">Kode merchant dari dashboard Tripay.</p>
        </div>
        <div className="flex items-center gap-4">
          <Label>Mode:</Label>
          <Button variant={form.mode === 'sandbox' ? 'default' : 'outline'} size="sm" onClick={() => setForm({ ...form, mode: 'sandbox' })} className={form.mode === 'sandbox' ? 'bg-amber-500' : ''}>Sandbox</Button>
          <Button variant={form.mode === 'production' ? 'default' : 'outline'} size="sm" onClick={() => setForm({ ...form, mode: 'production' })} className={form.mode === 'production' ? 'bg-emerald-500' : ''}>Production</Button>
        </div>

        {testResult && (
          <div className={`p-3 rounded-lg flex items-start gap-2 ${testResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {testResult.success ? <CheckCircle size={16} className="mt-0.5 shrink-0" /> : <XCircle size={16} className="mt-0.5 shrink-0" />}
            <div>
              <p>{testResult.message}</p>
              {testResult.data?.active_channels?.length > 0 && (
                <p className="text-xs mt-1 opacity-80">Channel aktif: {testResult.data.active_channels.join(', ')}</p>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button onClick={() => onSave(form)} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={saving}>
            {saving && <Loader2 size={14} className="animate-spin mr-2" />} Simpan
          </Button>
          <Button variant="outline" onClick={testConnection} disabled={testing || !form.api_key}>
            {testing ? <Loader2 size={14} className="animate-spin mr-2" /> : <Zap size={14} className="mr-2" />} Test Koneksi
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PaymentMethodsSettings() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchMethods();
  }, []);

  const fetchMethods = async () => {
    try {
      const res = await api.get('/tenant/integrations/payment-methods');
      setMethods(res.data.methods || []);
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal memuat metode pembayaran', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const toggleMethod = async (code, isActive) => {
    try {
      await api.put('/tenant/integrations/payment-methods', { method_code: code, is_active: isActive });
      setMethods(methods.map(m => m.code === code ? { ...m, is_active: isActive } : m));
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal mengubah metode', variant: 'destructive' });
    }
  };

  const groupedMethods = methods.reduce((acc, m) => {
    if (!acc[m.group]) acc[m.group] = [];
    acc[m.group].push(m);
    return acc;
  }, {});

  const getGroupIcon = (group) => {
    if (group === 'Virtual Account') return Building;
    if (group === 'E-Wallet') return Wallet;
    if (group === 'Retail') return Store;
    return CreditCard;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" /></div>;

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Wallet size={18} className="text-emerald-500" /> Metode Pembayaran</CardTitle>
        <CardDescription>Aktifkan atau nonaktifkan metode pembayaran yang tersedia</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {Object.entries(groupedMethods).map(([group, items]) => {
          const GroupIcon = getGroupIcon(group);
          return (
            <div key={group}>
              <div className="flex items-center gap-2 mb-3">
                <GroupIcon size={16} className="text-muted-foreground" />
                <h3 className="font-medium">{group}</h3>
                <Badge variant="outline" className="text-xs">{items.filter(i => i.is_active).length}/{items.length}</Badge>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {items.map((method) => (
                  <div 
                    key={method.code}
                    className={`p-3 border rounded-lg flex items-center justify-between ${method.is_active ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200'}`}
                  >
                    <div>
                      <p className="font-medium text-sm">{method.name}</p>
                    </div>
                    <Switch 
                      checked={method.is_active}
                      onCheckedChange={(v) => toggleMethod(method.code, v)}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function ResponicSettings({ data, onSave, saving }) {
  const [form, setForm] = useState({ api_key: data.api_key || '', is_active: data.is_active || false });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    setForm({ api_key: data.api_key || '', is_active: data.is_active || false });
  }, [data.api_key, data.is_active]);

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/tenant/integrations/test-connection', {
        integration_type: 'responic',
        config: form,
      });
      setTestResult(res.data);
      if (res.data.success) {
        toast({ title: 'Berhasil', description: res.data.message || 'Koneksi Responic berhasil' });
      } else {
        toast({ title: 'Gagal', description: res.data.message, variant: 'destructive' });
      }
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Gagal test koneksi' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><MessageSquare size={18} className="text-emerald-500" /> WhatsApp - Responic</CardTitle>
        <CardDescription>Kirim notifikasi WhatsApp otomatis ke customer via Responic</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!form.api_key && (
          <div className="p-3 rounded-lg bg-blue-50 text-blue-700 text-sm flex items-center gap-2">
            <CheckCircle size={16} />
            WA Gateway otomatis menggunakan konfigurasi admin (Responic platform). Isi API Token di bawah jika ingin menggunakan akun Responic sendiri.
          </div>
        )}
        <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /><Label>Aktifkan Responic</Label></div>
        <div>
          <Label>API Token <span className="text-muted-foreground text-xs">(kosongkan untuk pakai gateway admin)</span></Label>
          <Input value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="Bearer token Responic (opsional)" data-testid="responic-apikey-input" type="password" />
          <p className="text-xs text-muted-foreground mt-1">Dapatkan di Responic dashboard → Account → Pilih Account → Api Token.</p>
        </div>

        {testResult && (
          <div className={`p-3 rounded-lg flex items-center gap-2 ${testResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {testResult.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
            {testResult.message}
          </div>
        )}

        <div className="flex gap-2">
          <Button onClick={() => onSave(form)} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={saving} data-testid="save-responic-btn">
            {saving && <Loader2 size={14} className="animate-spin mr-2" />} Simpan
          </Button>
          <Button variant="outline" onClick={testConnection} disabled={testing} data-testid="test-responic-btn">
            {testing ? <Loader2 size={14} className="animate-spin mr-2" /> : <Zap size={14} className="mr-2" />} Test Koneksi
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EmailSettings({ data, onSave, saving }) {
  const [form, setForm] = useState({ 
    email_mode: data.email_mode || 'smtp',
    mailketing_api_token: data.mailketing_api_token || '',
    smtp_host: data.smtp_host || '', 
    smtp_port: data.smtp_port || 587, 
    smtp_username: data.smtp_username || '', 
    smtp_password: data.smtp_password || '', 
    sender_name: data.sender_name || '', 
    sender_email: data.sender_email || '', 
    is_active: data.is_active || false 
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const { toast } = useToast();

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/tenant/integrations/test-connection', { integration_type: 'myerketing' });
      setTestResult(res.data);
      if (res.data.success) {
        toast({ title: 'Berhasil', description: 'Koneksi email berhasil' });
      } else {
        toast({ title: 'Gagal', description: res.data.message, variant: 'destructive' });
      }
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Gagal test koneksi' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Mail size={18} className="text-violet-500" /> Email Notification</CardTitle>
        <CardDescription>Kirim email transaksional dan notifikasi via Mailketing API atau SMTP</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /><Label>Aktifkan Email</Label></div>
        
        <div>
          <Label>Mode Pengiriman</Label>
          <select className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm mt-1" value={form.email_mode} onChange={(e) => setForm({ ...form, email_mode: e.target.value })}>
            <option value="mailketing">Mailketing API</option>
            <option value="smtp">SMTP</option>
          </select>
        </div>

        {form.email_mode === 'mailketing' && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg border border-blue-200 bg-blue-50">
              <p className="text-sm text-blue-700">Kirim email menggunakan <a href="https://mailketing.co.id" target="_blank" rel="noopener noreferrer" className="underline font-medium">Mailketing API</a>. Masukkan API token dari dashboard Mailketing.</p>
            </div>
            <div><Label>API Token</Label><Input type="password" value={form.mailketing_api_token} onChange={(e) => setForm({ ...form, mailketing_api_token: e.target.value })} placeholder="Masukkan API token Mailketing" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Sender Name</Label><Input value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} placeholder="Nama Toko" /></div>
              <div><Label>Sender Email</Label><Input value={form.sender_email} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} placeholder="noreply@example.com" /></div>
            </div>
          </div>
        )}

        {form.email_mode === 'smtp' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>SMTP Host</Label><Input value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} placeholder="smtp.gmail.com" data-testid="smtp-host-input" /></div>
              <div><Label>Port</Label><Input type="number" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: parseInt(e.target.value) || 587 })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Username</Label><Input value={form.smtp_username} onChange={(e) => setForm({ ...form, smtp_username: e.target.value })} /></div>
              <div><Label>Password</Label><Input type="password" value={form.smtp_password} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Sender Name</Label><Input value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} /></div>
              <div><Label>Sender Email</Label><Input value={form.sender_email} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} /></div>
            </div>
          </div>
        )}
        
        {testResult && (
          <div className={`p-3 rounded-lg flex items-center gap-2 ${testResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {testResult.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
            {testResult.message}
          </div>
        )}

        <div className="flex gap-2">
          <Button onClick={() => onSave(form)} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={saving} data-testid="save-smtp-btn">
            {saving && <Loader2 size={14} className="animate-spin mr-2" />} Simpan
          </Button>
          <Button variant="outline" onClick={testConnection} disabled={testing || (!form.smtp_host && !form.mailketing_api_token)} data-testid="test-smtp-btn">
            {testing ? <Loader2 size={14} className="animate-spin mr-2" /> : <Zap size={14} className="mr-2" />} Test Koneksi
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


function MootaSettings() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [banks, setBanks] = useState([]);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState('');
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [customSecret, setCustomSecret] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tenant/integrations/moota');
      setCfg(data);
      if (data.is_configured) {
        try {
          const r = await api.post('/tenant/integrations/moota/banks');
          setBanks(r.data.banks || []);
        } catch { setBanks([]); }
        try {
          const w = await api.get('/tenant/integrations/moota/webhook-info');
          setWebhookInfo(w.data);
        } catch { setWebhookInfo(null); }
      }
    } catch {
      toast({ title: 'Error', description: 'Gagal memuat konfigurasi Moota', variant: 'destructive' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveToken = async () => {
    if (!token.trim()) return toast({ title: 'Error', description: 'Token wajib diisi', variant: 'destructive' });
    setBusy(true); setAction('token');
    try {
      const { data } = await api.put('/tenant/integrations/moota/token', { api_token: token.trim() });
      toast({ title: 'Sukses', description: `Token tersimpan. ${data.banks?.length || 0} rekening ditemukan.` });
      setBanks(data.banks || []);
      setToken('');
      await load();
    } catch (e) {
      toast({ title: 'Token tidak valid', description: e.response?.data?.detail || 'Gagal verifikasi token', variant: 'destructive' });
    } finally { setBusy(false); setAction(''); }
  };

  const refreshBanks = async () => {
    setBusy(true); setAction('refresh');
    try {
      const { data } = await api.post('/tenant/integrations/moota/banks');
      setBanks(data.banks || []);
      toast({ title: 'Sinkronisasi', description: `${data.banks?.length || 0} rekening` });
    } catch (e) {
      toast({ title: 'Gagal', description: e.response?.data?.detail || 'Gagal ambil bank', variant: 'destructive' });
    } finally { setBusy(false); setAction(''); }
  };

  const selectBank = async (b) => {
    setBusy(true); setAction(`bank-${b.bank_account_id}`);
    try {
      await api.put('/tenant/integrations/moota/select-bank', {
        bank_account_id: b.bank_account_id,
        bank_label: `${b.label} - ${b.account_number} (${b.account_holder})`,
      });
      toast({ title: 'Bank dipilih', description: b.label });
      await load();
    } catch (e) {
      toast({ title: 'Gagal', description: e.response?.data?.detail || 'Gagal simpan', variant: 'destructive' });
    } finally { setBusy(false); setAction(''); }
  };

  const registerWebhook = async () => {
    if (!cfg?.bank_account_id) {
      return toast({ title: 'Pilih rekening dulu', description: 'Pilih rekening bank sebelum setup webhook', variant: 'destructive' });
    }
    setBusy(true); setAction('webhook');
    try {
      const { data } = await api.post('/tenant/integrations/moota/register-webhook');
      toast({
        title: data.mode === 'auto' ? 'Webhook auto-terdaftar' : 'Webhook siap',
        description: data.message,
      });
      await load();
    } catch (e) {
      toast({ title: 'Gagal register webhook', description: e.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setBusy(false); setAction(''); }
  };

  const confirmWebhook = async () => {
    setBusy(true); setAction('confirm');
    try {
      const { data } = await api.post('/tenant/integrations/moota/confirm-webhook');
      toast({ title: 'Webhook aktif', description: data.message });
      await load();
    } catch (e) {
      toast({ title: 'Gagal konfirmasi', description: e.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setBusy(false); setAction(''); }
  };

  const saveWebhookSecret = async () => {
    if (!customSecret.trim()) {
      return toast({ title: 'Secret wajib diisi', variant: 'destructive' });
    }
    setBusy(true); setAction('secret');
    try {
      await api.put('/tenant/integrations/moota/webhook-secret', { webhook_secret: customSecret.trim() });
      toast({ title: 'Secret diperbarui', description: 'Pastikan secret sama dengan yang di dashboard Moota' });
      setCustomSecret('');
      await load();
    } catch (e) {
      toast({ title: 'Gagal simpan secret', description: e.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setBusy(false); setAction(''); }
  };

  const removeWebhook = async () => {
    if (!window.confirm('Hapus webhook Moota? Order tidak akan auto-update setelah ini.')) return;
    setBusy(true); setAction('rmwh');
    try {
      await api.delete('/tenant/integrations/moota/webhook');
      toast({ title: 'Webhook dihapus' });
      await load();
    } catch (e) {
      toast({ title: 'Gagal', description: e.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setBusy(false); setAction(''); }
  };

  const toggleActive = async () => {
    setBusy(true); setAction('toggle');
    try {
      await api.put('/tenant/integrations/moota/toggle', { is_active: !cfg.is_active });
      await load();
    } catch (e) {
      toast({ title: 'Gagal', description: e.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setBusy(false); setAction(''); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Moota? Token + bank + webhook akan dihapus dari Tokomoe.')) return;
    setBusy(true); setAction('disconnect');
    try {
      await api.delete('/tenant/integrations/moota');
      setBanks([]);
      await load();
      toast({ title: 'Moota disconnected' });
    } catch (e) {
      toast({ title: 'Gagal', description: e.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setBusy(false); setAction(''); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="animate-spin" /></div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Banknote size={20} className="text-emerald-600" /> Moota — Auto Verifikasi Mutasi Bank
          {cfg?.is_configured && (
            <Badge className={cfg.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
              {cfg.is_active ? 'Aktif' : 'Nonaktif'}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Hubungkan akun <a href="https://app.moota.co" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">Moota</a> agar transfer customer ke rekening Anda otomatis ter-verifikasi & order ter-update jadi paid.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5"><Zap size={13} /> Moota API Token (V2)</Label>
          {cfg?.is_configured ? (
            <div className="flex items-center gap-2">
              <Input value={cfg.api_token_masked} readOnly className="font-mono text-xs bg-slate-50" />
              <Button variant="outline" size="sm" onClick={disconnect} disabled={busy} className="text-red-600">
                <Trash2 size={13} className="mr-1" /> Disconnect
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Input value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste token dari app.moota.co - API Token" type="password" className="font-mono text-xs" />
              <Button onClick={saveToken} disabled={busy || !token.trim()} className="bg-emerald-600 hover:bg-emerald-700">
                {busy && action === 'token' ? <Loader2 size={14} className="animate-spin" /> : 'Hubungkan'}
              </Button>
            </div>
          )}
          {cfg?.last_error && <p className="text-xs text-red-600 flex items-center gap-1"><XCircle size={12} /> {cfg.last_error}</p>}
        </div>

        {cfg?.is_configured && (
          <div className="space-y-2 border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5"><Building size={13} /> Pilih Rekening untuk Terima Transfer</Label>
              <Button variant="outline" size="sm" onClick={refreshBanks} disabled={busy}>
                {busy && action === 'refresh' ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
                Sync
              </Button>
            </div>
            {banks.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-3 text-center">Belum ada rekening - tambah di app.moota.co lalu klik Sync.</p>
            ) : (
              <div className="space-y-1.5">
                {banks.map((b) => {
                  const isSelected = cfg.bank_account_id === b.bank_account_id;
                  return (
                    <div key={b.bank_account_id} className={`flex items-center justify-between p-3 rounded-lg border ${isSelected ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{b.label} - {b.account_number}</p>
                        <p className="text-xs text-slate-500 truncate">a.n. {b.account_holder} - Saldo: Rp {Number(b.balance || 0).toLocaleString('id-ID')}</p>
                      </div>
                      {isSelected ? (
                        <Badge className="bg-emerald-100 text-emerald-700 gap-1"><CheckCircle size={11} /> Aktif</Badge>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => selectBank(b)} disabled={busy}>
                          {busy && action === `bank-${b.bank_account_id}` ? <Loader2 size={11} className="animate-spin" /> : 'Pilih'}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {cfg?.is_configured && (
          <div className="space-y-2 border-t border-slate-100 pt-4">
            <Label>Webhook Auto-Match</Label>
            {!cfg.bank_account_id && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Pilih rekening bank terlebih dahulu sebelum setup webhook.
              </p>
            )}
            {!cfg.webhook_url ? (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">
                  Generate URL + Secret Token, lalu pasang di dashboard Moota (Integrasi → Webhook).
                </p>
                <Button onClick={registerWebhook} disabled={busy || !cfg.bank_account_id} className="bg-emerald-600 hover:bg-emerald-700">
                  {busy && action === 'webhook' ? <Loader2 size={13} className="animate-spin mr-2" /> : <Zap size={13} className="mr-2" />}
                  Siapkan Webhook
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs space-y-2">
                  <p className="font-semibold text-amber-900">Pasang webhook di Moota dashboard:</p>
                  <ol className="list-decimal pl-5 space-y-1 text-amber-900">
                    <li>Buka <a href="https://app.moota.co/integrations/webhook" target="_blank" rel="noopener noreferrer" className="underline">app.moota.co/integrations/webhook</a></li>
                    <li>Klik <strong>Tambah Webhook</strong></li>
                    <li>Paste <strong>Webhook URL</strong> dan <strong>Secret Token</strong> dari Tokomoe (bukan API Token)</li>
                    <li>Type/kinds: <strong>credit</strong> → Save</li>
                  </ol>
                </div>
                <div>
                  <Label className="text-xs">Webhook URL</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input value={cfg.webhook_url} readOnly className="font-mono text-[11px] bg-slate-50" />
                    <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(cfg.webhook_url); toast({ title: 'URL disalin' }); }}>
                      <Copy size={13} />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Secret Token (paste ke Moota, bukan API Token)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input value={webhookInfo?.webhook_secret || ''} readOnly className="font-mono text-[11px] bg-slate-50" />
                    <Button variant="outline" size="icon" onClick={() => { if (webhookInfo?.webhook_secret) { navigator.clipboard.writeText(webhookInfo.webhook_secret); toast({ title: 'Secret disalin' }); } }}>
                      <Copy size={13} />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {cfg.webhook_mode === 'auto' ? (
                    <Badge className="bg-emerald-100 text-emerald-700 gap-1"><CheckCircle size={11} /> Auto-terdaftar di Moota</Badge>
                  ) : cfg.webhook_installed ? (
                    <Badge className="bg-emerald-100 text-emerald-700 gap-1"><CheckCircle size={11} /> Webhook sudah dipasang</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">Menunggu dipasang di Moota</Badge>
                  )}
                  {!cfg.webhook_installed && (
                    <Button onClick={confirmWebhook} disabled={busy} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                      {busy && action === 'confirm' ? <Loader2 size={12} className="animate-spin mr-1" /> : <CheckCircle size={12} className="mr-1" />}
                      Sudah pasang di Moota
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={removeWebhook} disabled={busy} className="text-red-600 ml-auto">
                    <Trash2 size={12} className="mr-1" /> Reset
                  </Button>
                </div>
                <div className="border-t border-slate-100 pt-3 space-y-2">
                  <Label className="text-xs text-slate-500">Secret berbeda di Moota? Update manual:</Label>
                  <div className="flex items-center gap-2">
                    <Input value={customSecret} onChange={(e) => setCustomSecret(e.target.value)} placeholder="Paste secret dari dashboard Moota" type="password" className="font-mono text-[11px]" />
                    <Button variant="outline" size="sm" onClick={saveWebhookSecret} disabled={busy || !customSecret.trim()}>
                      {busy && action === 'secret' ? <Loader2 size={12} className="animate-spin" /> : 'Simpan'}
                    </Button>
                  </div>
                </div>
                {cfg.last_webhook_at && (
                  <p className="text-[11px] text-slate-400">Webhook terakhir diterima: {new Date(cfg.last_webhook_at).toLocaleString('id-ID')}</p>
                )}
              </div>
            )}
          </div>
        )}

        {cfg?.is_configured && (
          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <div>
              <Label>Status Integrasi</Label>
              <p className="text-xs text-slate-500">Nonaktifkan untuk berhenti memproses webhook tanpa hapus konfigurasi.</p>
            </div>
            <Switch checked={!!cfg.is_active} onCheckedChange={toggleActive} disabled={busy} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
